import { useState, useEffect } from "react";
import axios from "axios";
import { ShieldCheck, CircleNotch, ArrowsClockwise, Pulse, Warning, CheckCircle, XCircle, Key } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SourceBadge = ({ source }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
      source === "broker" ? "bg-[#ECFDF3] text-[#027A48]" : "bg-[#FFFAEB] text-[#B54708]"
    }`}
  >
    {source === "broker" ? "Broker" : ".env fallback"}
  </span>
);

const ResultBadge = ({ identity }) => {
  if (identity.breaker_open) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#B42318]">
        <Warning size={13} weight="fill" /> Broker disabled for this account
      </span>
    );
  }
  if (identity.last_observed_result === "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#027A48]">
        <CheckCircle size={13} weight="fill" /> Authenticating OK
      </span>
    );
  }
  if (identity.last_observed_result === "auth_failed") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#B42318]">
        <XCircle size={13} weight="fill" /> SAP refused the logon (401)
      </span>
    );
  }
  return <span className="text-[11px] text-[#98A2B3]">No SAP traffic observed yet</span>;
};

export default function SapCredentialHealthPage() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(null);
  const [results, setResults] = useState({});

  const loadHealth = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sap-credential-health`);
      setHealth(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load SAP credential health");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealth();
    const timer = setInterval(loadHealth, 30000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/admin/sap-credential-health/refresh`);
      setHealth(data);
      toast.success(`Pulled ${data.cached_identities} credentials from the broker`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broker refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleTest = async (brokerKey) => {
    setTesting(brokerKey);
    try {
      const { data } = await axios.post(`${API}/admin/sap-credential-health/test`, {
        broker_keys: brokerKey ? [brokerKey] : null,
      });
      setHealth(data.health);
      const next = { ...results };
      data.results.forEach((r) => { next[r.broker_key] = r; });
      setResults(next);
      const failed = data.results.filter((r) => r.result === "auth_failed");
      if (failed.length) toast.error(`${failed.map((r) => r.broker_key).join(", ")} refused by SAP`);
      else toast.success("SAP accepted the logon");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(null);
    }
  };

  const handleResetBreaker = async (brokerKey) => {
    try {
      const { data } = await axios.post(`${API}/admin/sap-credential-health/reset-breaker`, { broker_keys: [brokerKey] });
      setHealth(data);
      toast.success(`${brokerKey} will use the broker again`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="sap-credential-health-page">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Administration</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">SAP Credentials</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-4 sm:p-6 max-w-5xl w-full mx-auto">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <ShieldCheck size={20} weight="fill" className="text-[#0E7C86]" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">SAP Credential Health</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button
              onClick={handleRefresh}
              disabled={refreshing || !health?.broker_enabled}
              variant="outline"
              className="h-9 shrink-0"
              data-testid="sap-cred-refresh-broker-btn"
            >
              {refreshing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              <span className="ml-1.5">Pull from broker</span>
            </Button>
            <Button
              onClick={() => handleTest(null)}
              disabled={!!testing}
              className="h-9 bg-[#0E7C86] hover:bg-[#0B6B74] shrink-0"
              data-testid="sap-cred-test-all-btn"
            >
              {testing ? <CircleNotch size={14} className="animate-spin" /> : <Pulse size={14} weight="bold" />}
              <span className="ml-1.5">Test all</span>
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#667085] mb-5">
          SAP passwords come from the shared credential broker in OMS, so changing one there takes effect here within
          10 minutes with no redeploy. This table is <strong>passive</strong> - it reports what real SAP traffic has
          already seen, and makes no SAP calls on its own. Each <strong>Test</strong> is one real SAP logon attempt
          (limited to one per account per minute), because ByD locks an account after a handful of failed logons.
        </p>

        {loading ? (
          <p className="text-sm text-[#667085]" data-testid="sap-cred-loading">Loading...</p>
        ) : !health ? (
          <p className="text-sm text-[#667085]">Unavailable.</p>
        ) : (
          <>
            <div
              className={`mb-5 rounded-xl border p-4 flex items-start gap-3 ${
                health.broker_enabled ? "bg-white border-[#E4E7EC]" : "bg-[#FFFAEB] border-[#FEC84B]"
              }`}
              data-testid="sap-cred-broker-status"
            >
              <Key size={18} weight="fill" className={health.broker_enabled ? "text-[#0E7C86] mt-0.5" : "text-[#B54708] mt-0.5"} />
              <div className="text-sm">
                {health.broker_enabled ? (
                  <>
                    <p className="font-bold text-[#101828]">
                      Broker active - {health.cached_identities} credentials cached
                    </p>
                    <p className="text-[#667085] text-xs mt-0.5">
                      {health.broker_url} · TTL {health.ttl_seconds}s · last fetch{" "}
                      {health.last_fetch_at ? new Date(health.last_fetch_at).toLocaleString() : "never"}
                    </p>
                    {health.last_fetch_error && (
                      <p className="text-[#B42318] text-xs mt-1" data-testid="sap-cred-fetch-error">
                        Last fetch failed: {health.last_fetch_error} - running on the last good values / .env.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-bold text-[#B54708]">Broker not configured</p>
                    <p className="text-[#667085] text-xs mt-0.5">
                      <code>SAP_CRED_BROKER_KEY</code> is unset, so every SAP client is using its static{" "}
                      <code>.env</code> password. Nothing is broken - this is the safe fallback.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">SAP Account</TableHead>
                    <TableHead className="text-xs">Password Source</TableHead>
                    <TableHead className="text-xs">Last Observed</TableHead>
                    <TableHead className="text-xs">Last Test</TableHead>
                    <TableHead className="text-xs w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {health.identities.map((it) => (
                    <TableRow key={it.broker_key} data-testid={`sap-cred-row-${it.broker_key}`}>
                      <TableCell>
                        <div className="font-mono text-sm font-bold text-[#101828]">{it.sap_username}</div>
                        <div className="text-[11px] text-[#667085]">{it.label}</div>
                      </TableCell>
                      <TableCell>
                        <SourceBadge source={it.password_source} />
                        {!it.has_env_fallback && (
                          <div className="text-[11px] text-[#B54708] mt-1">No .env fallback</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <ResultBadge identity={it} />
                        {it.last_observed_at && (
                          <div className="text-[11px] text-[#98A2B3] mt-0.5">
                            {new Date(it.last_observed_at).toLocaleString()}
                          </div>
                        )}
                        {it.consecutive_auth_failures > 0 && (
                          <div className="text-[11px] text-[#B42318] mt-0.5">
                            {it.consecutive_auth_failures} consecutive 401s
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-[#667085] max-w-[220px]">
                        {results[it.broker_key] ? (
                          <span data-testid={`sap-cred-test-result-${it.broker_key}`}>{results[it.broker_key].detail}</span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1.5 items-start">
                          <button
                            onClick={() => handleTest(it.broker_key)}
                            disabled={testing === it.broker_key}
                            className="text-[11px] font-bold text-[#0E7C86] hover:underline disabled:opacity-40"
                            data-testid={`sap-cred-test-${it.broker_key}`}
                          >
                            {testing === it.broker_key ? "Testing..." : "Test"}
                          </button>
                          {it.breaker_open && (
                            <button
                              onClick={() => handleResetBreaker(it.broker_key)}
                              className="text-[11px] font-bold text-[#B54708] hover:underline"
                              data-testid={`sap-cred-reset-${it.broker_key}`}
                            >
                              Re-enable broker
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
